/**
 * Welcome Offer Popup
 * -------------------------------------------------------------------
 * Replaces the Klaviyo onsite form. Posts to Shopify's own customer
 * endpoint so the address lands in Shopify first and nowhere else, then
 * a Shopify Flow keyed off the applied customer tag mints the discount
 * code and sends the email.
 *
 * The form submits natively rather than over fetch. When captcha is
 * enabled on customer forms, Shopify's captcha script hooks the form's
 * submit event to inject its token. Intercepting the submit strips the
 * token and Shopify rejects the post with "Invalid CAPTCHA token", so we
 * let the browser and the captcha script do their work. Shopify
 * redirects back with ?customer_posted=true and we reopen the popup on
 * the success panel.
 */

const WELCOME_OFFER_STORAGE_KEY = 'upton-welcome-offer';
const WELCOME_OFFER_PENDING_KEY = 'upton-welcome-offer-pending';

// Keep in sync with the opacity/transform transition duration in
// welcome-offer-popup.css.
const WELCOME_OFFER_CLOSE_DURATION = 250;

const WELCOME_OFFER_FOCUSABLE =
  'a[href], button:enabled, input:not([type="hidden"]):enabled, select:enabled, textarea:enabled, [tabindex]:not([tabindex^="-"])';

const WELCOME_OFFER_COMPLIANCE_HOST = '.captain-compliance-modal-container';
const WELCOME_OFFER_COMPLIANCE_UI =
  '.captain-compliance-gpc-banner, #cc-modal-cookies-banner, #cc-modal-cookies-banner-settings';
const WELCOME_OFFER_COMPLIANCE_EXEMPT =
  `${WELCOME_OFFER_COMPLIANCE_HOST}, ${WELCOME_OFFER_COMPLIANCE_UI}`;

class WelcomeOfferPopup extends HTMLElement {
  constructor() {
    super();

    this.dialog = this.querySelector('.welcome-offer__dialog');
    this.form = this.querySelector('form');
    this.emailInput = this.querySelector('.welcome-offer__input');
    this.errorEl = this.querySelector('[data-wo-error]');
    this.submitButton = this.querySelector('[data-wo-submit]');
    this.submitLabel = this.querySelector('[data-wo-submit-label]');
    this.formPanel = this.querySelector('[data-wo-panel="form"]');
    this.successPanel = this.querySelector('[data-wo-panel="success"]');

    this.designMode = this.dataset.designMode === 'true';
    this.delay = parseInt(this.dataset.delay, 10) || 0;
    this.dismissDays = parseInt(this.dataset.dismissDays, 10) || 30;
    this.successDays = parseInt(this.dataset.successDays, 10) || 365;

    this.openTimer = null;
    this.closeTimer = null;
    this.lastFocused = null;
    this.inertElements = [];
    this.isSubscribed = false;
    this.isSubmitting = false;
    this.previousLabel = '';

    this.onKeydown = this.onKeydown.bind(this);
    this.onComplianceInteraction = this.onComplianceInteraction.bind(this);
  }

  connectedCallback() {
    this.complianceObserver = new MutationObserver(() => this.syncCompliance());
    this.syncCompliance();
    document.addEventListener('click', this.onComplianceInteraction, true);
    document.addEventListener('focusin', this.onComplianceInteraction);

    this.querySelector('[data-wo-cookie-preferences]')?.addEventListener('click', () => {
      const launcher = this.complianceLauncher();
      if (!launcher) return;
      this.pauseForCompliance();
      launcher.click();
      this.focusCompliance();
    });

    this.querySelectorAll('[data-wo-close]').forEach((el) => {
      el.addEventListener('click', () => this.dismiss());
    });

    document.querySelectorAll('[data-welcome-offer-open]').forEach((el) => {
      el.addEventListener('click', (event) => {
        event.preventDefault();
        this.open();
      });
    });

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }

    // A native post redirects back here with ?customer_posted=true.
    if (this.resumePendingSubmission()) return;

    if (this.designMode) {
      this.open();
      return;
    }

    if (!this.shouldShow()) return;

    if (this.delay > 0) {
      this.openTimer = window.setTimeout(() => this.open(), this.delay * 1000);
    } else {
      this.open();
    }
  }

  disconnectedCallback() {
    this.complianceObserver.disconnect();
    document.removeEventListener('click', this.onComplianceInteraction, true);
    document.removeEventListener('focusin', this.onComplianceInteraction);
    this.close({ immediate: true, restoreFocus: false });
    window.clearTimeout(this.openTimer);
    window.clearTimeout(this.closeTimer);
    document.removeEventListener('keydown', this.onKeydown);
    this.releaseBackground();
  }

  /* Display rules -------------------------------------------------- */

  shouldShow() {
    const params = new URLSearchParams(window.location.search);

    if (params.has('welcome-offer-preview')) return true;
    if (params.get('customer_posted') === 'true') return false;
    if (this.isExcludedPath()) return false;
    if (this.isSuppressed()) return false;

    return true;
  }

  isExcludedPath() {
    const raw = this.dataset.excludedPaths || '';
    const path = window.location.pathname.toLowerCase();

    return raw
      .split(',')
      .map((entry) => entry.trim().toLowerCase())
      .filter(Boolean)
      .some((entry) => path.startsWith(entry));
  }

  isSuppressed() {
    const record = this.readRecord();
    if (!record) return false;

    if (typeof record.until !== 'number') return false;
    if (Date.now() >= record.until) {
      this.clearRecord();
      return false;
    }

    return true;
  }

  /* Storage -------------------------------------------------------- */

  readRecord() {
    try {
      const raw = window.localStorage.getItem(WELCOME_OFFER_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (error) {
      return null;
    }
  }

  writeRecord(state, days) {
    try {
      window.localStorage.setItem(
        WELCOME_OFFER_STORAGE_KEY,
        JSON.stringify({ state, until: Date.now() + days * 24 * 60 * 60 * 1000 })
      );
    } catch (error) {
      // Private browsing or storage disabled. Nothing to persist.
    }
  }

  clearRecord() {
    try {
      window.localStorage.removeItem(WELCOME_OFFER_STORAGE_KEY);
    } catch (error) {
      // No-op.
    }
  }

  /* Open and close ------------------------------------------------- */

  complianceRoots() {
    return [document, ...Array.from(document.querySelectorAll(WELCOME_OFFER_COMPLIANCE_HOST))
      .map((host) => host.shadowRoot).filter(Boolean)];
  }

  visibleComplianceElements() {
    return this.complianceRoots().flatMap((root) =>
      Array.from(root.querySelectorAll(WELCOME_OFFER_COMPLIANCE_UI)).filter((element) =>
        window.getComputedStyle(element).visibility === 'visible' && element.getClientRects().length > 0
      )
    );
  }

  complianceHasFocus() {
    const host = document.activeElement?.closest(WELCOME_OFFER_COMPLIANCE_EXEMPT);
    const active = host?.shadowRoot?.activeElement || host;
    return active && window.getComputedStyle(active).visibility === 'visible' && active.getClientRects().length > 0;
  }

  complianceLauncher() {
    return this.complianceRoots().map((root) => root.querySelector('#cc-cookie-simple-button_id')).find(Boolean);
  }

  focusCompliance() {
    const target = this.visibleComplianceElements()
      .flatMap((panel) => Array.from(panel.querySelectorAll(WELCOME_OFFER_FOCUSABLE)))
      .find((element) => window.getComputedStyle(element).visibility === 'visible' && element.getClientRects().length > 0);
    target?.focus();
  }

  syncCompliance() {
    // Document observers cannot see into Captain Compliance's open shadow root.
    // Observe it separately, including when the vendor inserts it after we open.
    this.complianceRoots().forEach((root) => this.complianceObserver.observe(root, {
      subtree: true, childList: true, attributes: true, attributeFilter: ['style', 'class', 'hidden'],
    }));

    const preferencesButton = this.querySelector('[data-wo-cookie-preferences]');
    if (preferencesButton) {
      const hidden = !this.complianceLauncher();
      if (preferencesButton.hidden !== hidden) preferencesButton.hidden = hidden;
    }

    if (this.hidden) return;

    if (this.visibleComplianceElements().length) {
      this.pauseForCompliance();
    } else {
      // A newly inserted host may be inside a region we previously made inert.
      this.releaseBackground();
      this.lockBackground();
    }
  }

  onComplianceInteraction(event) {
    if (!this.hidden && event.composedPath().some((node) =>
      node instanceof Element && node.matches(WELCOME_OFFER_COMPLIANCE_EXEMPT)
    )) this.pauseForCompliance();
  }

  pauseForCompliance() {
    const focusWasInside = this.contains(document.activeElement);
    // Yield immediately, without a fade or a stored dismissal, so consent
    // controls own clicks, focus and Escape. Resume when they are finished.
    this.close({ immediate: true, restoreFocus: false });
    if (focusWasInside) this.focusCompliance();
    this.openTimer = window.setTimeout(() => this.open(), 250);
  }

  open() {
    window.clearTimeout(this.openTimer);
    if (!this.isConnected) return;

    if (this.visibleComplianceElements().length || this.complianceHasFocus()) {
      this.openTimer = window.setTimeout(() => this.open(), 250);
      return;
    }

    window.clearTimeout(this.closeTimer);

    // Reopened mid-fade: the close never finished, so just fade back in.
    if (!this.hidden) {
      this.classList.add('is-visible');
      return;
    }

    if (!document.activeElement?.closest(WELCOME_OFFER_COMPLIANCE_EXEMPT)) {
      this.lastFocused = document.activeElement;
    }
    this.hidden = false;

    // Let the browser paint the hidden state before transitioning in.
    window.requestAnimationFrame(() => {
      if (!this.hidden) this.classList.add('is-visible');
    });

    // Horizon locks page scroll with this attribute (see base.css).
    document.documentElement.setAttribute('scroll-lock', '');
    document.addEventListener('keydown', this.onKeydown);
    this.lockBackground();

    this.focusTarget().focus();
  }

  /** Whichever panel is showing owns the initial focus. */
  focusTarget() {
    if (this.successPanel && !this.successPanel.hidden) {
      return this.successPanel.querySelector('.welcome-offer__offer') || this.dialog;
    }

    // Back from Shopify with an error: land on the field, which is
    // described by the message, so the two are read together.
    if (this.emailInput && this.errorEl && !this.errorEl.hidden) {
      return this.emailInput;
    }

    // The popup opens on a timer, not on request. Starting on the dialog
    // lets screen readers announce the offer before anything else, and
    // keeps phones from raising the keyboard over it unasked.
    return this.dialog;
  }

  /**
   * Locks the background while leaving consent controls and their ancestors
   * interactive. Recurse through shared wrappers to lock unrelated content.
   */
  lockBackground() {
    const lock = (element) => {
      if (element.inert || element.matches(WELCOME_OFFER_COMPLIANCE_EXEMPT)) return;
      if (element.querySelector(WELCOME_OFFER_COMPLIANCE_EXEMPT)) {
        Array.from(element.children).forEach(lock);
        return;
      }
      element.inert = true;
      this.inertElements.push(element);
    };
    let node = this;

    while (node.parentElement && node !== document.body) {
      Array.from(node.parentElement.children).forEach((sibling) => {
        if (sibling !== node) lock(sibling);
      });

      node = node.parentElement;
    }
  }

  /** Only undoes what lockBackground set, so other inert regions stay put. */
  releaseBackground() {
    this.inertElements.forEach((el) => {
      el.inert = false;
    });

    this.inertElements = [];
  }

  close({ immediate = false, restoreFocus = true } = {}) {
    window.clearTimeout(this.openTimer);

    if (this.hidden) return;

    window.clearTimeout(this.closeTimer);
    this.classList.remove('is-visible');

    const reducedMotion = immediate || window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    // Scroll stays locked, the page stays inert and focus stays put until
    // the element is actually hidden, or releasing them mid-fade lets the
    // page scroll and refocus behind the still-visible popup, reading as
    // a jump.
    const finish = () => {
      this.hidden = true;
      document.documentElement.removeAttribute('scroll-lock');
      document.removeEventListener('keydown', this.onKeydown);

      // Must come before restoring focus, which can't land on an inert
      // element. `preventScroll` keeps the page from jumping to wherever
      // focus used to be.
      this.releaseBackground();

      if (restoreFocus && this.lastFocused && typeof this.lastFocused.focus === 'function') {
        this.lastFocused.focus({ preventScroll: true });
      }
    };

    if (reducedMotion) {
      finish();
    } else {
      this.closeTimer = window.setTimeout(finish, WELCOME_OFFER_CLOSE_DURATION);
    }
  }

  /** Close and remember the choice, unless we are in the theme editor. */
  dismiss() {
    if (!this.designMode && !this.isSubscribed) {
      this.writeRecord('dismissed', this.dismissDays);
    }

    this.close();
  }

  onKeydown(event) {
    if (this.visibleComplianceElements().length || this.complianceHasFocus()) {
      this.pauseForCompliance();
      return;
    }

    if (event.key === 'Escape' || event.code === 'Escape') {
      this.dismiss();
      return;
    }

    if (event.key === 'Tab') this.wrapFocus(event);
  }

  /**
   * Cycles Tab and Shift+Tab within the dialog. Handled here rather than
   * with Horizon's trapFocus, which fixes its first and last elements at
   * open and counts the link in the hidden success panel, so Shift+Tab
   * from the close button had nowhere to go.
   */
  wrapFocus(event) {
    const focusable = this.focusableElements();

    if (!focusable.length) {
      event.preventDefault();
      this.dialog.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active = document.activeElement;
    const outside = !this.dialog.contains(active);

    if (event.shiftKey && (active === first || active === this.dialog || outside)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (active === last || outside)) {
      event.preventDefault();
      first.focus();
    }
  }

  /** Controls in the panel that's showing. Hidden ones have no boxes. */
  focusableElements() {
    return Array.from(this.dialog.querySelectorAll(WELCOME_OFFER_FOCUSABLE)).filter(
      (el) => el.getClientRects().length > 0
    );
  }

  /* Submission ----------------------------------------------------- */

  /**
   * Validates locally, then stands aside. Nothing here may call
   * preventDefault on a valid submission, or Shopify's captcha script
   * never gets to attach its token.
   */
  onSubmit(event) {
    if (this.isSubmitting) {
      event.preventDefault();
      return;
    }

    if (!this.emailInput || !this.emailInput.checkValidity()) {
      event.preventDefault();
      this.showError('Please enter a valid email address.');
      if (this.emailInput) this.emailInput.focus();
      return;
    }

    this.clearError();
    this.isSubmitting = true;
    this.setBusy(true);
    this.markPending();

    // Native submission continues from here.
  }

  /** Flags the intent so the success panel shows once Shopify returns. */
  markPending() {
    try {
      window.sessionStorage.setItem(WELCOME_OFFER_PENDING_KEY, '1');
    } catch (error) {
      // Private browsing or storage disabled. Nothing to persist.
    }
  }

  resumePendingSubmission() {
    let pending = false;

    try {
      pending = window.sessionStorage.getItem(WELCOME_OFFER_PENDING_KEY) === '1';
      window.sessionStorage.removeItem(WELCOME_OFFER_PENDING_KEY);
    } catch (error) {
      return false;
    }

    if (!pending) return false;

    const params = new URLSearchParams(window.location.search);

    if (params.get('customer_posted') === 'true') {
      this.onSuccess();
      return true;
    }

    // Came back with validation errors rendered into the form.
    if (this.errorEl && !this.errorEl.hidden) {
      this.open();
      return true;
    }

    return false;
  }

  onSuccess() {
    this.isSubscribed = true;
    this.writeRecord('subscribed', this.successDays);

    this.setBusy(false);

    if (this.formPanel) this.formPanel.hidden = true;
    if (this.successPanel) this.successPanel.hidden = false;

    // The dialog is named by whichever panel is showing.
    if (this.dataset.successLabelledby) {
      this.dialog.setAttribute('aria-labelledby', this.dataset.successLabelledby);
    }

    this.open();

    document.dispatchEvent(new CustomEvent('welcome-offer:subscribed'));
  }

  /* UI helpers ----------------------------------------------------- */

  /**
   * Deliberately does not set `disabled`. A disabled submit button drops
   * its own name/value from the post, and this one carries `commit`.
   * Re-entry is guarded by `isSubmitting` instead.
   */
  setBusy(isBusy) {
    if (!this.submitButton) return;

    this.submitButton.setAttribute('aria-busy', isBusy ? 'true' : 'false');

    if (!this.submitLabel) return;

    if (isBusy) {
      this.previousLabel = this.submitLabel.textContent;
      this.submitLabel.textContent = 'Sending...';
    } else if (this.previousLabel) {
      this.submitLabel.textContent = this.previousLabel;
    }
  }

  showError(message) {
    if (this.emailInput) {
      this.emailInput.setAttribute('aria-invalid', 'true');
      if (this.errorEl) this.emailInput.setAttribute('aria-describedby', this.errorEl.id);
    }

    if (!this.errorEl) return;

    this.errorEl.textContent = message;
    this.errorEl.hidden = false;
  }

  clearError() {
    if (this.emailInput) {
      this.emailInput.removeAttribute('aria-invalid');
      this.emailInput.removeAttribute('aria-describedby');
    }

    if (!this.errorEl) return;

    this.errorEl.textContent = '';
    this.errorEl.hidden = true;
  }
}

if (!customElements.get('welcome-offer-popup')) {
  customElements.define('welcome-offer-popup', WelcomeOfferPopup);
}
